import { MOTION_SPRINGS } from './kit/tokens.js';

export interface CardDragMotion {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  /**
   * Fly-home phase after a missed release: a gentler spring without the
   * drag-lag clamp, so the whole travel back to the hand slot stays visible
   * instead of snapping to within MAX_DRAG_LAG of the target on frame one.
   */
  returning?: boolean;
  /**
   * Lift-off phase right after a pickup: the preview starts exactly over the
   * grabbed card and springs to its above-the-pointer anchor. The drag-lag
   * clamp stays off until the preview first catches up, otherwise the travel
   * would snap to within MAX_DRAG_LAG on frame one — the "teleport" jump.
   */
  liftoff?: boolean;
  velocityX: number;
  velocityY: number;
  rotateX: number;
  rotateY: number;
  rotateZ: number;
  skewX: number;
  skewY: number;
  scaleX: number;
  scaleY: number;
  originX: number;
  originY: number;
  glareX: number;
  glareY: number;
  glareOpacity: number;
  phase: number;
  baseRotation: number;
}

const MAX_HOVER_TILT = 10;
const MAX_DRAG_LAG = MOTION_SPRINGS.maxDragLag;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function degrees(value: number): string {
  return `${value.toFixed(2)}deg`;
}

/** Synthetic events (jsdom, exotic browsers) may yield undefined coordinates. */
function pixels(value: number): string {
  return `${(Number.isFinite(value) ? value : 0).toFixed(2)}px`;
}

/**
 * Applies a restrained pointer-following tilt without scheduling React state.
 * The card's CSS transition provides the soft return when the values reset.
 */
export function applyCardPointerMotion(
  element: HTMLElement,
  clientX: number,
  clientY: number,
): void {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;
  const x = clamp((clientX - rect.left) / rect.width, 0, 1);
  const y = clamp((clientY - rect.top) / rect.height, 0, 1);
  const normalizedX = x * 2 - 1;
  const normalizedY = y * 2 - 1;
  element.style.setProperty('--cards-motion-rotate-x', degrees(-normalizedY * MAX_HOVER_TILT));
  element.style.setProperty('--cards-motion-rotate-y', degrees(normalizedX * MAX_HOVER_TILT));
  // background-position semantics (the shared stepped sheen), matching
  // useCardTilt's glare vars: the light band follows the pointer.
  element.style.setProperty('--cards-motion-light-x', `${(x * 100).toFixed(1)}%`);
  element.style.setProperty('--cards-motion-light-y', `${(y * 100).toFixed(1)}%`);
  // The pointer's -1..1 position, for the art's parallax drift (see KIT_CSS).
  element.style.setProperty('--cards-motion-nx', normalizedX.toFixed(3));
  element.style.setProperty('--cards-motion-ny', normalizedY.toFixed(3));
  element.dataset.motionActive = 'true';
}

export function resetCardPointerMotion(element: HTMLElement): void {
  element.style.removeProperty('--cards-motion-rotate-x');
  element.style.removeProperty('--cards-motion-rotate-y');
  element.style.removeProperty('--cards-motion-light-x');
  element.style.removeProperty('--cards-motion-light-y');
  element.style.removeProperty('--cards-motion-nx');
  element.style.removeProperty('--cards-motion-ny');
  delete element.dataset.motionActive;
}

export function createCardDragMotion(
  x: number,
  y: number,
  baseRotation = 2,
  originX = 50,
  originY = 70,
): CardDragMotion {
  return {
    x,
    y,
    targetX: x,
    targetY: y,
    velocityX: 0,
    velocityY: 0,
    rotateX: 0,
    rotateY: 0,
    rotateZ: baseRotation,
    skewX: 0,
    skewY: 0,
    scaleX: 1,
    scaleY: 1,
    originX: clamp(originX, 0, 100),
    originY: clamp(originY, 0, 100),
    glareX: 50,
    glareY: 30,
    glareOpacity: 0.08,
    phase: 0,
    baseRotation,
  };
}

export function setCardDragTarget(motion: CardDragMotion, x: number, y: number): void {
  motion.targetX = x;
  motion.targetY = y;
}

/**
 * True where KIT_CSS switches the battle to its compact layout — the same query,
 * so anything JavaScript has to do differently there stays in step with the CSS.
 */
export function compactBattleLayout(): boolean {
  return globalThis.matchMedia?.('(max-width: 900px), (max-height: 520px) and (pointer: coarse)').matches === true;
}

/** Matches the .cards-touch-drag-preview anchor-gap breakpoints in KIT_CSS. */
export function previewAnchorGap(): number {
  return compactBattleLayout() ? 12 : 18;
}

/**
 * Starts a fresh pickup over the grabbed card itself: the preview's anchor
 * point is placed so it exactly covers the card's rect and the spring then
 * carries it to the pointer — the card visibly lifts out of its slot instead
 * of materializing above the finger.
 */
export function liftCardDragMotionFromRect(
  motion: CardDragMotion,
  rect: Pick<DOMRect, 'left' | 'width' | 'bottom'>,
  anchorGap: number,
): void {
  if (rect.width <= 0 || !Number.isFinite(rect.left) || !Number.isFinite(rect.bottom)) return;
  motion.x = rect.left + rect.width / 2;
  motion.y = rect.bottom + anchorGap;
  motion.liftoff = true;
}

/**
 * Advances a deliberately under-damped pointer follower. Lag creates the
 * stretch toward the finger; overshoot and speed-coupled flutter keep the card
 * alive even through a mostly straight drag.
 */
export function advanceCardDragMotion(motion: CardDragMotion, elapsedMs: number): void {
  const dt = clamp(elapsedMs / 1_000, 1 / 240, 1 / 30);
  const returning = motion.returning === true;
  const spring = returning ? MOTION_SPRINGS.returnStiffness : MOTION_SPRINGS.followStiffness;
  // The fly-home leg brakes progressively near the slot (up to near-critical
  // damping) so the card visibly stops on it before the landing fade begins,
  // instead of dissolving while still drifting.
  const remaining = Math.hypot(motion.targetX - motion.x, motion.targetY - motion.y);
  const damping = returning
    ? MOTION_SPRINGS.returnDampingFrom
      + (MOTION_SPRINGS.returnDampingTo - MOTION_SPRINGS.returnDampingFrom)
        * clamp(1 - remaining / MOTION_SPRINGS.returnBrakeDistance, 0, 1)
    : MOTION_SPRINGS.followDamping;
  const drag = Math.exp(-damping * dt);

  motion.velocityX = (motion.velocityX + (motion.targetX - motion.x) * spring * dt) * drag;
  motion.velocityY = (motion.velocityY + (motion.targetY - motion.y) * spring * dt) * drag;
  motion.x += motion.velocityX * dt;
  motion.y += motion.velocityY * dt;

  // The lag clamp keeps a dragged card glued near the pointer; a returning
  // card must be free to travel the whole way home visibly, and a fresh
  // pickup first travels from its slot to the pointer un-clamped.
  if (motion.liftoff === true
    && Math.hypot(motion.targetX - motion.x, motion.targetY - motion.y) <= MAX_DRAG_LAG) {
    motion.liftoff = false;
  }
  if (!returning && motion.liftoff !== true) {
    motion.x = clamp(motion.x, motion.targetX - MAX_DRAG_LAG, motion.targetX + MAX_DRAG_LAG);
    motion.y = clamp(motion.y, motion.targetY - MAX_DRAG_LAG, motion.targetY + MAX_DRAG_LAG);
  }

  const lagX = motion.targetX - motion.x;
  const lagY = motion.targetY - motion.y;
  const speed = Math.hypot(motion.velocityX, motion.velocityY);
  const activity = clamp((Math.hypot(lagX, lagY) + speed * 0.018) / 26, 0, 1);
  motion.phase += dt * (7 + activity * 9);
  const flutterX = Math.sin(motion.phase) * activity;
  const flutterY = Math.cos(motion.phase * 0.83) * activity;
  const follow = 1 - Math.exp(-13 * dt);

  const targetRotateX = clamp(-lagY * 0.24 - motion.velocityY * 0.0045 + flutterY * 1.4, -12, 12);
  const targetRotateY = clamp(lagX * 0.26 + motion.velocityX * 0.0048 + flutterX * 1.6, -13, 13);
  // A returning card levels out flat so it rests aligned with its hand slot.
  const targetRotateZ = (returning ? 0 : motion.baseRotation)
    + clamp(lagX * 0.16 + motion.velocityX * 0.004 + flutterX * 1.8, -11, 11);
  motion.rotateX += (targetRotateX - motion.rotateX) * follow;
  motion.rotateY += (targetRotateY - motion.rotateY) * follow;
  motion.rotateZ += (targetRotateZ - motion.rotateZ) * follow;

  const targetSkewX = clamp(lagX * 0.11 + flutterX * 1.2, -5.5, 5.5);
  const targetSkewY = clamp(-lagY * 0.07 + flutterY * 0.8, -3.5, 3.5);
  motion.skewX += (targetSkewX - motion.skewX) * follow;
  motion.skewY += (targetSkewY - motion.skewY) * follow;

  // A returning card relaxes to exactly 1: its ghost bakes the preview's CSS
  // scale bonus into the motion, so 1.0 lands pixel-identical to the slot card.
  const targetScaleX = returning ? 1 : clamp(1 + Math.abs(lagX) * 0.0028 - Math.abs(lagY) * 0.0007, 0.97, 1.085);
  const targetScaleY = returning ? 1 : clamp(1 + Math.abs(lagY) * 0.0022 - Math.abs(lagX) * 0.0005, 0.975, 1.07);
  motion.scaleX += (targetScaleX - motion.scaleX) * follow;
  motion.scaleY += (targetScaleY - motion.scaleY) * follow;

  motion.glareX = clamp(50 - lagX * 1.3, 12, 88);
  motion.glareY = clamp(42 - lagY * 0.9, 12, 88);
  motion.glareOpacity = 0.08 + activity * 0.34;
}

export function cardDragMotionVariables(motion: CardDragMotion): Record<string, string> {
  return {
    // Position rides the transform chain (translate3d in KIT_CSS), not
    // left/top: per-frame left/top writes forced a relayout of the whole
    // card face on every frame, while a transform stays on the compositor.
    '--cards-drag-x': pixels(motion.x),
    '--cards-drag-y': pixels(motion.y),
    '--cards-drag-rotate-x': degrees(motion.rotateX),
    '--cards-drag-rotate-y': degrees(motion.rotateY),
    '--cards-drag-rotate-z': degrees(motion.rotateZ),
    '--cards-drag-skew-x': degrees(motion.skewX),
    '--cards-drag-skew-y': degrees(motion.skewY),
    '--cards-drag-scale-x': motion.scaleX.toFixed(4),
    '--cards-drag-scale-y': motion.scaleY.toFixed(4),
    '--cards-drag-origin-x': `${motion.originX.toFixed(1)}%`,
    '--cards-drag-origin-y': `${motion.originY.toFixed(1)}%`,
    // The glare's gradient is a static oversized layer. Its centre moves by a
    // compositor transform, avoiding a fresh gradient paint on every frame.
    '--cards-drag-glare-shift-x': `${((motion.glareX - 50) / 2).toFixed(1)}%`,
    '--cards-drag-glare-shift-y': `${((motion.glareY - 50) / 2).toFixed(1)}%`,
    '--cards-drag-glare-opacity': motion.glareOpacity.toFixed(3),
  };
}

/**
 * Values last written to each element, so a frame that changed nothing writes
 * nothing. Every setProperty invalidates the element's style even when the value
 * is identical, and a spring at rest under a still finger produces the same
 * fourteen strings frame after frame — that was a recalc per frame for a card
 * that visibly is not moving.
 */
const lastDragVariables = new WeakMap<HTMLElement, Record<string, string>>();

export function applyCardDragMotion(element: HTMLElement, motion: CardDragMotion): void {
  const previous = lastDragVariables.get(element);
  const next = cardDragMotionVariables(motion);
  for (const [property, value] of Object.entries(next)) {
    if (previous?.[property] === value) continue;
    element.style.setProperty(property, value);
  }
  lastDragVariables.set(element, next);
}

export function startCardDragAnimation(
  element: HTMLElement,
  currentMotion: () => CardDragMotion | undefined,
): () => void {
  if (globalThis.requestAnimationFrame === undefined) return () => undefined;
  const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  let frame = 0;
  let previous = globalThis.performance?.now() ?? Date.now();
  const tick = (now: number): void => {
    const motion = currentMotion();
    if (motion === undefined) return;
    if (reduceMotion) {
      motion.x = motion.targetX;
      motion.y = motion.targetY;
      motion.velocityX = 0;
      motion.velocityY = 0;
      motion.rotateX = 0;
      motion.rotateY = 0;
      motion.rotateZ = motion.baseRotation;
      motion.skewX = 0;
      motion.skewY = 0;
      motion.scaleX = 1;
      motion.scaleY = 1;
      motion.glareOpacity = 0;
    } else {
      advanceCardDragMotion(motion, now - previous);
    }
    applyCardDragMotion(element, motion);
    previous = now;
    frame = globalThis.requestAnimationFrame(tick);
  };
  frame = globalThis.requestAnimationFrame(tick);
  return () => globalThis.cancelAnimationFrame?.(frame);
}
